import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { peekVisitorId } from "@/lib/analytics";
import { toast } from "@/lib/toast";
import { Crosshair, UserPlus, ShieldCheck, Loader2, Copy, Check } from "lucide-react";

// opticswap.com/i/<code> — the shareable personal invite link. Stores the code, counts a click,
// and shows the inviter's handle before routing into signup. Attribution is bound at register.
// A logged-in member sees an "already a member" state with their OWN link to share instead —
// opening a friend's link is usually the moment someone is about to share one themselves.
export default function InviteLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const c = (code || "").trim().toUpperCase();
  const [state, setState] = useState({ loading: true, valid: false, inviter: null });
  const [myLink, setMyLink] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (c) localStorage.setItem("os_invite_code", c);
    api.get(`/invites/resolve/${c}`, { params: { visitor_id: peekVisitorId() || undefined } })
      .then(({ data }) => setState({ loading: false, valid: !!data.valid, inviter: data.inviter_username || null }))
      .catch(() => setState({ loading: false, valid: false, inviter: null }));
  }, [c]);

  // Logged-in member: fetch their own permanent invite link to offer for sharing.
  useEffect(() => {
    if (!user) return;
    api.get("/invites/me").then(({ data }) => {
      if (data?.code) setMyLink(`${window.location.origin}/i/${data.code}`);
    }).catch(() => {});
  }, [user]);

  const copyMine = async () => {
    if (!myLink) return;
    try {
      await navigator.clipboard.writeText(myLink);
      setCopied(true);
      toast.success("Your invite link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — grab it from your Invites page.");
    }
  };

  return (
    <div className="min-h-screen text-foreground flex items-center justify-center px-4" data-testid="invite-landing">
      <div className="w-full max-w-md border border-sniper bg-surface p-8 text-center">
        <Crosshair className="w-10 h-10 mx-auto text-olive-bright" strokeWidth={1.5} />

        {state.loading ? (
          <div className="mt-6 flex items-center justify-center gap-2 text-muted-foreground font-mono uppercase text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your invite…
          </div>
        ) : user ? (
          /* ALREADY A MEMBER — don't bounce them into signup; hand them their own link. */
          <div data-testid="invite-already-member">
            {state.valid && (
              <div className="mt-5 inline-flex items-center gap-2 border border-olive/40 bg-olive/5 px-3 py-1.5" data-testid="invite-vouch">
                <UserPlus className="w-4 h-4 text-olive-bright" />
                <span className="font-mono text-xs uppercase">Invited by <span className="text-olive-bright">@{state.inviter}</span></span>
              </div>
            )}
            <h1 className="mt-5 font-heading text-2xl font-bold uppercase tracking-tight">You're already a member</h1>
            <p className="mt-3 text-sm font-body text-secondary-foreground">
              Signed in as <span className="text-foreground">@{user.username}</span>.
              {state.valid ? ` You don't need @${state.inviter}'s link — ` : " "}
              Share your own instead. Anyone who joins through it carries your handle on their profile.
            </p>
            <div className="mt-5 flex items-stretch border border-sniper bg-obsidian text-left">
              <span className="flex-1 min-w-0 px-3 py-2.5 font-mono text-sm text-foreground truncate" data-testid="invite-my-link">
                {myLink || "Loading your link…"}
              </span>
              <button onClick={copyMine} disabled={!myLink} data-testid="invite-copy-my-link"
                className="shrink-0 inline-flex items-center gap-1.5 border-l border-sniper bg-olive hover:bg-olive-bright disabled:opacity-40 text-white font-mono uppercase text-[11px] tracking-wide px-4 transition-colors">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="mt-5 flex items-center justify-center gap-4">
              <Link to="/invite" data-testid="invite-go-to-invites" className="font-mono text-xs uppercase text-olive-bright hover:text-volt">Manage your invites</Link>
              <Link to="/feed" className="font-mono text-xs uppercase text-muted-foreground hover:text-foreground">Marketplace</Link>
            </div>
          </div>
        ) : state.valid ? (
          /* NEW VISITOR, valid invite — the vouch is concrete: it names the stake. */
          <>
            <div className="mt-5 inline-flex items-center gap-2 border border-olive/40 bg-olive/5 px-3 py-1.5" data-testid="invite-vouch">
              <UserPlus className="w-4 h-4 text-olive-bright" />
              <span className="font-mono text-xs uppercase">Invited by <span className="text-olive-bright">@{state.inviter}</span></span>
            </div>
            <h1 className="mt-5 font-heading text-3xl font-bold uppercase tracking-tight">You're on the list</h1>
            <p className="mt-3 text-sm font-body text-secondary-foreground">
              OpticSwap is a verified-only marketplace for rifle scopes and shooting optics. Every member is
              ID-verified — no bots, no tire-kickers.
            </p>
            <p className="mt-3 text-sm font-body text-secondary-foreground border-t border-sniper/60 pt-3" data-testid="invite-vouch-detail">
              <span className="text-olive-bright">@{state.inviter}</span> vouched for you. Once you're ID-verified, their handle
              sits on your profile until your first sale — if you cause problems, it reflects on them.
            </p>
            <button onClick={() => navigate(`/register?ref=${c}`)} data-testid="invite-join-btn"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase py-3 transition-colors">
              <ShieldCheck className="w-4 h-4" /> Create my account
            </button>
            <Link to="/login" className="mt-3 inline-block font-mono text-xs uppercase text-muted-foreground hover:text-foreground">Already a member? Log in</Link>
          </>
        ) : (
          /* INVALID / EXPIRED invite */
          <>
            <h1 className="mt-5 font-heading text-2xl font-bold uppercase tracking-tight">Invite not found</h1>
            <p className="mt-3 text-sm font-body text-secondary-foreground">This invite link is invalid or has expired — but you can still join OpticSwap.</p>
            <button onClick={() => navigate("/register")} data-testid="invite-join-btn"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase py-3 transition-colors">
              Create my account
            </button>
          </>
        )}
      </div>
    </div>
  );
}
